import React, { useState, useMemo, useEffect } from "react";
import {
  Box, Button, Card, Typography,
  Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, CircularProgress,
  Skeleton, Snackbar, Alert, InputAdornment, Tooltip, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, Switch, FormControlLabel,
  TablePagination, Paper,
} from "@mui/material";
import {
  Plus, Pencil, Trash2, X, BookOpen, Type, Clock, Layers, GraduationCap,
  Tag, Upload, DollarSign, Monitor, Award, Users, Star, User, Search,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import AdminShell from "../layout/AdminShell";
import { adminGetCourses, adminGetCourseById, adminCreateCourse, adminUpdateCourse, adminDeleteCourse } from "../../api/courses";
import { adminGetUsers } from "../../api/users";
import { adminGetCourseCategories } from "../../api/Coursecategories";

// ── API-valid values ────────────────────────────────────────────────────────
const LEVELS = [
  { label: "Beginner",     value: "BEGINNER" },
  { label: "Intermediate", value: "INTERMEDIATE" },
  { label: "Professional", value: "PROFESSIONAL" },
  { label: "Expert",       value: "EXPERT" },
];

const CATEGORIES = [
  { label: "Cloud",        value: "CLOUD" },
  { label: "Security",     value: "SECURITY" },
  { label: "DevOps",       value: "DEVOPS" },
  { label: "Data Science", value: "DATA_SCIENCE" },
  { label: "AI / ML",      value: "AI_ML" },
  { label: "Networking",   value: "NETWORKING" },
  { label: "Other",        value: "OTHER" },
];

const MODES = [
  { label: "Online",    value: "ONLINE" },
  { label: "Classroom", value: "CLASSROOM" },
  { label: "Hybrid",    value: "HYBRID" },
];

const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];

// ── Style maps ──────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  ACTIVE:   { bg: "rgba(34,197,94,0.1)",  color: "#16A34A", dot: "#22C55E" },
  DRAFT:    { bg: "rgba(245,158,11,0.1)", color: "#B45309", dot: "#F59E0B" },
  INACTIVE: { bg: "rgba(239,68,68,0.1)",  color: "#DC2626", dot: "#EF4444" },
};

const LEVEL_STYLES = {
  BEGINNER:     { bg: "rgba(34,197,94,0.08)",  color: "#15803D", dot: "#22C55E" },
  INTERMEDIATE: { bg: "rgba(245,158,11,0.08)", color: "#B45309", dot: "#F59E0B" },
  PROFESSIONAL: { bg: "rgba(239,68,68,0.08)",  color: "#DC2626", dot: "#EF4444" },
  EXPERT:       { bg: "rgba(139,92,246,0.08)", color: "#7C3AED", dot: "#8B5CF6" },
};

const MODE_DOTS = { ONLINE: "#22C55E", CLASSROOM: "#F59E0B", HYBRID: "#8B5CF6" };

// ── Quill config ────────────────────────────────────────────────────────────
const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
  clipboard: { matchVisual: false },
};
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "link"];

// Quill reports an empty editor as "<p><br></p>" — treat that as empty.
const isQuillEmpty = (html) => !html || html.replace(/<(.|\n)*?>/g, "").trim() === "";

// ── Helpers ─────────────────────────────────────────────────────────────────
const slugify = (str, maxLength = 20) => {
  let slug = str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (slug.length > maxLength) {
    slug = slug.slice(0, maxLength);
    slug = slug.replace(/-[^-]*$/, "");
    slug = slug.replace(/-+$/, "");
  }
  return slug;
};

const formatINR = (v) => `₹${parseFloat(v).toLocaleString("en-IN")}`;

const getErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  return data?.message || data?.errors?.[0]?.message || data?.error || fallback;
};

const DEFAULT_VALUES = {
  title: "",
  subtitle: "",
  short_description: "",
  long_description: "",
  certification_type: "",
  category: "",
  level: "BEGINNER",
  mode: "ONLINE",
  duration_weeks: "",
  price: "",
  discount_price: "",
  rating: "",
  rating_count: "",
  students_enrolled: "",
  instructor: "",
  status: "DRAFT",
  label: "",
  is_featured: false,
  order_on_homepage: 0,
  sequence: "",
};

const HEAD_CELL_SX = {
  fontWeight: 700,
  fontSize: 11.5,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748B",
  bgcolor: "#F8FAFC",
  borderBottom: "1px solid rgba(15,23,42,0.08)",
  whiteSpace: "nowrap",
  py: 1.5,
};

const ICON_BTN_SX = (hoverColor, hoverBg) => ({
  border: "1px solid rgba(15,23,42,0.1)",
  borderRadius: "8px",
  "&:hover": { borderColor: hoverColor, color: hoverColor, bgcolor: hoverBg },
});

// ── Small presentational pieces ─────────────────────────────────────────────
function SectionCard({ accent, icon, title, children }) {
  return (
    <Paper elevation={0} sx={{
      p: 3,
      borderRadius: "16px",
      border: "1px solid rgba(15,23,42,0.06)",
      borderLeft: `4px solid ${accent}`,
      backgroundColor: "#FFFFFF",
      boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: "9px",
          backgroundColor: `${accent}1A`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>{title}</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
        {children}
      </Box>
    </Paper>
  );
}

const Col = ({ span = 12, sm, children }) => (
  <Box sx={{ gridColumn: sm ? { xs: "span 12", sm: `span ${sm}` } : `span ${span}` }}>{children}</Box>
);

const adorn = (icon) => ({
  startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
});

const Dot = ({ color }) => (
  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
);

// ── Component ────────────────────────────────────────────────────────────────
export default function CoursesPage() {
  const qc = useQueryClient();
  const [legacyLabel, setLegacyLabel] = useState(""); // TEMP: remove after migrating old labels
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    register, handleSubmit, reset, control,
    formState: { errors }, watch, setValue,
  } = useForm({ defaultValues: DEFAULT_VALUES });

  const cardImageField = register("card_image");

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // ── Dialog helpers ─────────────────────────────────────────────────────────
  // `force` lets mutation callbacks close the dialog even while the mutation
  // is still flagged as pending (onSuccess runs before isPending flips back).
  const closeDialog = (force = false) => {
    if (!force && (createMutation.isPending || updateMutation.isPending)) return;
    setOpen(false);
    setEditing(null);
    setThumbPreview(null);
    reset(DEFAULT_VALUES);
    setLegacyLabel("");
  };

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: apiCourses = [], isLoading } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: () => adminGetCourses().then((r) => r.data),
  });

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => adminGetUsers().then((r) => r.data),
  });

  const { data: labelData, isLoading: labelsLoading } = useQuery({
    queryKey: ["admin-course-categories"],
    queryFn: () => adminGetCourseCategories().then((r) => r.data),
  });

  const labelOptions = (
    Array.isArray(labelData?.data) ? labelData.data : Array.isArray(labelData) ? labelData : []
  ).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const courses = useMemo(() => (Array.isArray(apiCourses) ? apiCourses : []), [apiCourses]);

  const usersById = useMemo(() => {
    const map = {};
    (Array.isArray(users) ? users : []).forEach((u) => { map[u.id] = u; });
    return map;
  }, [users]);

  const statusCounts = useMemo(() => {
    const counts = { ALL: courses.length, ACTIVE: 0, DRAFT: 0, INACTIVE: 0 };
    courses.forEach((c) => { if (counts[c.status] != null) counts[c.status] += 1; });
    return counts;
  }, [courses]);

  const filteredCourses = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return courses.filter((c) => {
      if (statusFilter !== "ALL" && c.status !== statusFilter) return false;
      if (!q) return true;
      return (
        c.title?.toLowerCase().includes(q) ||
        c.category?.toLowerCase().includes(q) ||
        c.subtitle?.toLowerCase().includes(q)
      );
    });
  }, [courses, searchQuery, statusFilter]);

  const totalCount = filteredCourses.length;
  const paginatedCourses = filteredCourses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // If rows disappear (delete / filter) and the current page no longer exists, step back.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [totalCount, rowsPerPage, page]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (fd) => adminCreateCourse(fd),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-courses"] });
      notify("Course created");
      closeDialog(true);
    },
    onError: (err) => notify(getErrorMessage(err, "Couldn't create course"), "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateCourse(id, data),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-courses"] });
      notify("Course updated");
      closeDialog(true);
    },
    onError: (err) => notify(getErrorMessage(err, "Couldn't update course"), "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminDeleteCourse(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-courses"] });
      notify("Course deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(getErrorMessage(err, "Couldn't delete course"), "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    const fd = new FormData();

    // Only auto-generate the slug for brand-new courses so URLs don't shift on edit.
    fd.append("slug", editing ? (editing.slug || slugify(values.title)) : slugify(values.title));
    fd.append("title", values.title);

    ["subtitle", "short_description", "certification_type", "category", "level", "mode", "status", "label"]
      .forEach((k) => {
        if (values[k] !== undefined && values[k] !== "") fd.append(k, values[k]);
      });

    if (!isQuillEmpty(values.long_description)) fd.append("long_description", values.long_description);

    [
      "duration_weeks", "price", "discount_price", "rating", "rating_count",
      "students_enrolled", "instructor", "order_on_homepage", "sequence",
    ].forEach((k) => {
      if (values[k] !== "" && values[k] != null) fd.append(k, values[k]);
    });

    fd.append("is_featured", values.is_featured ? "true" : "false");

    if (values.card_image?.[0]) fd.append("card_image", values.card_image[0]);

    if (editing) updateMutation.mutate({ id: editing.id, data: fd });
    else createMutation.mutate(fd);
  };

  const openCreate = () => {
    setEditing(null);
    setThumbPreview(null);
    reset(DEFAULT_VALUES);
    setLegacyLabel("");
    setOpen(true);
  };

  const openEdit = async (c) => {
    setLoadingEditId(c.id);
    try {
      const { data } = await adminGetCourseById(c.id);
      setEditing(data);
      setLegacyLabel(data.label ?? "");
      setThumbPreview(data.card_image_url || null);
      reset({
        title: data.title ?? "",
        subtitle: data.subtitle ?? "",
        short_description: data.short_description ?? "",
        long_description: data.long_description ?? "",
        certification_type: data.certification_type ?? "",
        category: data.category ?? "",
        level: data.level ?? "BEGINNER",
        mode: data.mode ?? "ONLINE",
        duration_weeks: data.duration_weeks ?? "",
        price: data.price ?? "",
        discount_price: data.discount_price ?? "",
        rating: data.rating ?? "",
        rating_count: data.rating_count ?? "",
        students_enrolled: data.students_enrolled ?? "",
        instructor: data.instructor ?? "",
        status: data.status ?? "DRAFT",
        label: data.label ?? "",
        is_featured: data.is_featured ?? false,
        order_on_homepage: data.order_on_homepage ?? 0,
        sequence: data.sequence ?? "",
      });
      setOpen(true);
    } catch (err) {
      notify(getErrorMessage(err, "Couldn't load course details"), "error");
    } finally {
      setLoadingEditId(null);
    }
  };

  const handleThumbChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setThumbPreview(URL.createObjectURL(file));
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleStatusFilter = (s) => {
    setStatusFilter(s);
    setPage(0);
  };

  const getInstructorName = (c) => {
    const inst = c.instructor;
    if (!inst) return null;
    if (typeof inst === "object") return inst.full_name || inst.email || null;
    const u = usersById[inst];
    return u ? (u.full_name || u.email) : null;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AdminShell title="Courses">
      {/* Page header */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5, mb: 2.5,
      }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0F172A", letterSpacing: "-0.01em" }}>
            Courses
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.25, maxWidth: 480 }}>
            Courses listed on the public site. Set status to Active to make a course visible to visitors.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          Add Course
        </Button>
      </Box>

      {/* Toolbar: search + status filters */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        p: 2, mb: 2.5,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        gap: 2,
      }}>
        <TextField
          size="small"
          placeholder="Search by title, subtitle or category…"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search size={18} color="#94A3B8" /></InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setSearchQuery(""); setPage(0); }}>
                  <X size={14} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#F8FAFC" },
          }}
        />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {["ALL", ...STATUSES].map((s) => {
            const active = statusFilter === s;
            const style = STATUS_STYLES[s];
            return (
              <Chip
                key={s}
                clickable
                onClick={() => handleStatusFilter(s)}
                label={`${s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()} · ${statusCounts[s]}`}
                sx={{
                  fontWeight: 700, fontSize: 12.5, height: 30, borderRadius: "8px",
                  border: "1px solid",
                  borderColor: active ? (style?.color || "#2563EB") : "rgba(15,23,42,0.12)",
                  bgcolor: active ? (style?.bg || "rgba(37,99,235,0.08)") : "transparent",
                  color: active ? (style?.color || "#2563EB") : "#475569",
                }}
              />
            );
          })}
        </Box>
      </Card>

      {/* Table */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        overflow: "hidden",
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>Course</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Category</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Level</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Mode</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Instructor</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Duration</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Price</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                <TableCell sx={{ ...HEAD_CELL_SX, position: "sticky", right: 0 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" width={j === 0 ? 200 : 70} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedCourses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: "14px",
                      bgcolor: "rgba(37,99,235,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 1.5,
                    }}>
                      <BookOpen size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>No courses found</Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery || statusFilter !== "ALL"
                        ? "Try adjusting your search or filters"
                        : "Add your first course to publish it on the public site."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCourses.map((c) => {
                  const statusStyle = STATUS_STYLES[c.status] || STATUS_STYLES.DRAFT;
                  const levelStyle = LEVEL_STYLES[c.level];
                  const modeLabel = MODES.find((m) => m.value === c.mode)?.label;
                  const instructorName = getInstructorName(c);
                  const hasDiscount =
                    c.discount_price != null && c.discount_price !== "" &&
                    parseFloat(c.discount_price) > 0 &&
                    parseFloat(c.discount_price) < parseFloat(c.price);
                  const isEditLoading = loadingEditId === c.id;

                  return (
                    <TableRow
                      key={c.id}
                      hover
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                        "& td": { borderBottom: "1px solid rgba(15,23,42,0.06)", py: 1.5 },
                      }}
                    >
                      {/* Course — thumbnail + title only */}
                      <TableCell sx={{ minWidth: 260, maxWidth: 340 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            variant="rounded"
                            src={c.card_image_url || undefined}
                            sx={{
                              width: 44, height: 44, borderRadius: "10px",
                              bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", flexShrink: 0,
                            }}
                          >
                            <BookOpen size={18} />
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Tooltip title={c.title} placement="top-start" enterDelay={600}>
                              <Typography
                                sx={{
                                  fontWeight: 700, fontSize: 14, color: "#0F172A", lineHeight: 1.35,
                                  display: "-webkit-box", WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical", overflow: "hidden",
                                }}
                              >
                                {c.title}
                              </Typography>
                            </Tooltip>
                            {c.is_featured && (
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.4 }}>
                                <Star size={12} color="#F59E0B" fill="#F59E0B" />
                                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#B45309" }}>
                                  Featured
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {c.category ? (
                          <Chip
                            label={CATEGORIES.find((cat) => cat.value === c.category)?.label || c.category}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: 11.5, height: 24, bgcolor: "rgba(37,99,235,0.07)", color: "#2563EB" }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 13.5, color: "text.disabled" }}>—</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {c.level ? (
                          <Chip
                            label={LEVELS.find((l) => l.value === c.level)?.label || c.level}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: 11.5, height: 24, bgcolor: levelStyle?.bg, color: levelStyle?.color }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 13.5, color: "text.disabled" }}>—</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {modeLabel ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Dot color={MODE_DOTS[c.mode] || "#94A3B8"} />
                            <Typography sx={{ fontSize: 13.5, color: "#334155" }}>{modeLabel}</Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 13.5, color: "text.disabled" }}>—</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap", maxWidth: 180 }}>
                        <Typography sx={{ fontSize: 13.5, color: instructorName ? "#334155" : "text.disabled" }} noWrap>
                          {instructorName || "—"}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ fontSize: 13.5, color: "#334155", whiteSpace: "nowrap" }}>
                        {c.duration_weeks != null && c.duration_weeks !== ""
                          ? `${c.duration_weeks} wk${Number(c.duration_weeks) !== 1 ? "s" : ""}`
                          : "—"}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {c.price != null && c.price !== "" ? (
                          hasDiscount ? (
                            <Box>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A", lineHeight: 1.2 }}>
                                {formatINR(c.discount_price)}
                              </Typography>
                              <Typography sx={{ fontSize: 11.5, color: "text.secondary", textDecoration: "line-through" }}>
                                {formatINR(c.price)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A" }}>
                              {formatINR(c.price)}
                            </Typography>
                          )
                        ) : (
                          <Typography sx={{ fontSize: 13.5, color: "text.disabled" }}>—</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Chip
                          icon={<Box sx={{ ml: "8px !important" }}><Dot color={statusStyle.dot} /></Box>}
                          label={c.status || "DRAFT"}
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: 11, height: 24,
                            bgcolor: statusStyle.bg, color: statusStyle.color,
                            "& .MuiChip-icon": { color: "inherit", display: "flex" },
                          }}
                        />
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{ position: "sticky", right: 0, bgcolor: "background.paper" }}
                      >
                        <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                          <Tooltip title="Edit">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openEdit(c)}
                                disabled={loadingEditId != null}
                                sx={ICON_BTN_SX("#2563EB", "rgba(37,99,235,0.05)")}
                              >
                                {isEditLoading ? <CircularProgress size={14} /> : <Pencil size={14} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setConfirmDelete(c)}
                              sx={ICON_BTN_SX("#EF4444", "rgba(239,68,68,0.05)")}
                            >
                              <Trash2 size={14} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {!isLoading && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid rgba(15,23,42,0.08)",
              "& .MuiTablePagination-toolbar": { padding: "8px 24px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "13.5px", color: "text.secondary",
              },
              "& .MuiInputBase-root": { borderRadius: "8px", border: "1px solid rgba(15,23,42,0.15)" },
            }}
          />
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog
        open={open}
        onClose={() => closeDialog()}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow: "0 30px 70px rgba(15,23,42,0.22)",
          },
        }}
      >
        <DialogTitle sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: 2.5, px: 3,
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 55%, #3B82F6 100%)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <BookOpen size={22} color="#FFFFFF" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                {editing ? "Edit Course" : "Add New Course"}
              </Typography>
              <Typography noWrap sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", fontWeight: 500, mt: 0.25 }}>
                {editing ? `Editing: ${editing.title}` : "Create a new course for your catalog"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => closeDialog()}
            disabled={saving}
            sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{
            display: "flex", flexDirection: "column", gap: 2.5,
            pt: 3, pb: 2, px: 3,
            maxHeight: "65vh", overflowY: "auto",
            backgroundColor: "#F1F5F9",
          }}>
            {/* COURSE DETAILS */}
            <SectionCard accent="#2563EB" icon={<BookOpen size={17} color="#2563EB" />} title="Course Details">
              <Col>
                <TextField
                  label="Course Title"
                  fullWidth
                  disabled={saving}
                  required
                  error={!!errors.title}
                  helperText={errors.title ? "Title is required" : "Enter the main title of the course"}
                  InputProps={adorn(<Type size={16} color="#94A3B8" />)}
                  InputLabelProps={{ shrink: true }}
                  {...register("title", { required: true })}
                />
              </Col>
              <Col sm={4}>
  <TextField
    label="Sequence"
    fullWidth
    disabled={saving}
    type="number"
    placeholder="e.g., 1, 2, 3"
    helperText="Display order of the course"
    inputProps={{ min: 0, step: 1 }}
    InputProps={adorn(<Layers size={16} color="#94A3B8" />)}
    InputLabelProps={{ shrink: true }}
    {...register("sequence", { min: 0 })}
  />
</Col>

              <Col sm={6}>
                <TextField
                  label="Subtitle"
                  fullWidth
                  disabled={saving}
                  placeholder="e.g., AWS Certified Solutions Architect"
                  InputProps={adorn(<Tag size={16} color="#94A3B8" />)}
                  InputLabelProps={{ shrink: true }}
                  {...register("subtitle")}
                />
              </Col>

              <Col sm={6}>
                <Controller
                  name="label"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label="Course-Category"
                      fullWidth
                      disabled={saving || labelsLoading}
                      InputProps={adorn(<Star size={16} color="#94A3B8" />)}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      // only show a value the dropdown actually contains
                      value={labelOptions.some((o) => o.label === field.value) ? field.value : ""}
                      // TEMP: the "Current label" text goes away once old labels are migrated
                      helperText={
                        editing && legacyLabel
                          ? `Current label: "${legacyLabel}". Pick the matching one from the list.`
                          : "Choose a label from the list"
                      }
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {labelOptions.map((o) => (
                        <MenuItem key={o.id} value={o.label}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Col>

              <Col>
                <TextField
                  label="Short Description"
                  fullWidth
                  disabled={saving}
                  placeholder="Brief summary that appears on course cards"
                  InputLabelProps={{ shrink: true }}
                  {...register("short_description")}
                />
              </Col>

              <Col>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 0.75 }}>
                  Full Description
                </Typography>
                <Controller
                  name="long_description"
                  control={control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        bgcolor: "#FFFFFF",
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: "1px solid rgba(0,0,0,0.23)",
                        "& .ql-toolbar": { borderRadius: "10px 10px 0 0", borderColor: "transparent", borderBottom: "1px solid rgba(0,0,0,0.1)" },
                        "& .ql-container": { borderColor: "transparent", fontSize: 14.5, minHeight: 180 },
                        "& .ql-editor": { minHeight: 180 },
                      }}
                    >
                      <ReactQuill
                        ref={(el) => {
                          if (el && !el.__pasteHandlerAttached) {
                            const editor = el.getEditor();
                            editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
                              // Drop inline formatting from pasted content — keep plain text only.
                              const Delta = editor.constructor.import("delta");
                              const plain = new Delta();
                              delta.ops.forEach((op) => {
                                if (typeof op.insert === "string") plain.insert(op.insert);
                              });
                              return plain;
                            });
                            el.__pasteHandlerAttached = true;
                          }
                        }}
                        theme="snow"
                        value={field.value || ""}
                        onChange={field.onChange}
                        readOnly={saving}
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                        placeholder="Detailed course overview, prerequisites, what students will learn…"
                      />
                    </Box>
                  )}
                />
              </Col>
            </SectionCard>

            {/* CATEGORY & DIFFICULTY */}
            <SectionCard accent="#7C3AED" icon={<Layers size={17} color="#7C3AED" />} title="Category & Difficulty">
              <Col sm={6}>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label="Category"
                      fullWidth
                      disabled={saving}
                      InputProps={adorn(<Layers size={16} color="#94A3B8" />)}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      value={field.value ?? ""}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {CATEGORIES.map((c) => (
                        <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Col>

              <Col sm={6}>
                <Controller
                  name="level"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label="Level"
                      fullWidth
                      disabled={saving}
                      InputProps={adorn(<GraduationCap size={16} color="#94A3B8" />)}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      value={field.value ?? ""}
                    >
                      {LEVELS.map((l) => (
                        <MenuItem key={l.value} value={l.value}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Dot color={LEVEL_STYLES[l.value]?.dot} />
                            {l.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Col>

              <Col sm={6}>
                <Controller
                  name="mode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label="Delivery Mode"
                      fullWidth
                      disabled={saving}
                      InputProps={adorn(<Monitor size={16} color="#94A3B8" />)}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      value={field.value ?? ""}
                    >
                      {MODES.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Dot color={MODE_DOTS[m.value]} />
                            {m.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Col>

              <Col sm={6}>
                <TextField
                  label="Certification Type"
                  fullWidth
                  disabled={saving}
                  placeholder="e.g., AWS-SAA, AZ-104, PMP"
                  InputProps={adorn(<Award size={16} color="#94A3B8" />)}
                  InputLabelProps={{ shrink: true }}
                  {...register("certification_type")}
                />
              </Col>
            </SectionCard>

            {/* PRICING & DURATION */}
            <SectionCard accent="#16A34A" icon={<DollarSign size={17} color="#16A34A" />} title="Pricing & Duration">
              <Col sm={6}>
                <TextField
                  label="Price (₹)"
                  fullWidth
                  disabled={saving}
                  type="number"
                  required
                  error={!!errors.price}
                  helperText={errors.price ? "Price is required" : ""}
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={adorn("₹")}
                  InputLabelProps={{ shrink: true }}
                  {...register("price", { required: true, min: 0 })}
                />
              </Col>

              <Col sm={6}>
                <TextField
                  label="Discount Price (₹)"
                  fullWidth
                  disabled={saving}
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={adorn("₹")}
                  InputLabelProps={{ shrink: true }}
                  {...register("discount_price", { min: 0 })}
                />
              </Col>

              <Col sm={6}>
                <TextField
                  label="Duration (Weeks)"
                  fullWidth
                  disabled={saving}
                  type="number"
                  placeholder="e.g., 6, 12, 24"
                  inputProps={{ min: 1, step: 1 }}
                  InputProps={adorn(<Clock size={16} color="#94A3B8" />)}
                  InputLabelProps={{ shrink: true }}
                  {...register("duration_weeks", { min: 1 })}
                />
              </Col>
            </SectionCard>

            {/* INSTRUCTOR & RATINGS */}
            <SectionCard accent="#EA580C" icon={<User size={17} color="#EA580C" />} title="Instructor & Ratings">
              <Col>
                <Controller
                  name="instructor"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label="Instructor"
                      fullWidth
                      disabled={saving || usersLoading}
                      InputProps={adorn(<User size={16} color="#94A3B8" />)}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      value={field.value ?? ""}
                    >
                      <MenuItem value=""><em>Select an instructor</em></MenuItem>
                      {(Array.isArray(users) ? users : [])
                        .filter((u) => u.is_active)
                        .map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar
                                src={u.profile_image || undefined}
                                sx={{ width: 24, height: 24, bgcolor: "rgba(37,99,235,0.15)", color: "#2563EB", fontSize: 12 }}
                              >
                                {u.full_name?.charAt(0) || u.email?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                                  {u.full_name || u.email}
                                </Typography>
                                {u.email && u.full_name && (
                                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{u.email}</Typography>
                                )}
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />
              </Col>

              <Col sm={4}>
                <TextField
                  label="Rating"
                  fullWidth
                  disabled={saving}
                  type="number"
                  placeholder="0.0 - 5.0"
                  inputProps={{ min: 0, max: 5, step: "0.01" }}
                  InputProps={adorn(<Star size={16} color="#94A3B8" />)}
                  InputLabelProps={{ shrink: true }}
                  {...register("rating", { min: 0, max: 5 })}
                />
              </Col>

              <Col sm={4}>
                <TextField
                  label="Rating Count"
                  fullWidth
                  disabled={saving}
                  type="number"
                  placeholder="Total ratings"
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={adorn(<Users size={16} color="#94A3B8" />)}
                  InputLabelProps={{ shrink: true }}
                  {...register("rating_count", { min: 0 })}
                />
              </Col>

              <Col sm={4}>
                <TextField
                  label="Students Enrolled"
                  fullWidth
                  disabled={saving}
                  type="number"
                  placeholder="Total students"
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={adorn(<Users size={16} color="#94A3B8" />)}
                  InputLabelProps={{ shrink: true }}
                  {...register("students_enrolled", { min: 0 })}
                />
              </Col>
            </SectionCard>

            {/* DISPLAY SETTINGS */}
            <SectionCard accent="#0891B2" icon={<Monitor size={17} color="#0891B2" />} title="Display Settings">
              <Col sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label="Status"
                      fullWidth
                      disabled={saving}
                      InputProps={adorn(<Tag size={16} color="#94A3B8" />)}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      value={field.value ?? ""}
                    >
                      {STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Dot color={STATUS_STYLES[s]?.dot} />
                            {s}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Col>

              <Col sm={6}>
                <TextField
                  label="Homepage Order"
                  fullWidth
                  disabled={saving}
                  type="number"
                  placeholder="0 = first"
                  inputProps={{ min: 0, step: 1 }}
                  InputLabelProps={{ shrink: true }}
                  {...register("order_on_homepage", { min: 0 })}
                />
              </Col>

              <Col>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!watch("is_featured")}
                      onChange={(e) => setValue("is_featured", e.target.checked)}
                      disabled={saving}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#2563EB" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2563EB" },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Star size={16} color="#94A3B8" />
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                        Featured on homepage
                      </Typography>
                    </Box>
                  }
                />
              </Col>

              <Col>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", mb: 1.5 }}>
                  Course Thumbnail
                </Typography>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 2, p: 2,
                  borderRadius: "12px",
                  border: "2px dashed rgba(37,99,235,0.2)",
                  backgroundColor: "rgba(37,99,235,0.03)",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: "#2563EB", backgroundColor: "rgba(37,99,235,0.05)" },
                }}>
                  <Avatar
                    variant="rounded"
                    src={thumbPreview || undefined}
                    sx={{ width: 64, height: 64, borderRadius: "12px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", flexShrink: 0 }}
                  >
                    <BookOpen size={24} />
                  </Avatar>
                  <Box>
                    <Button
                      component="label"
                      variant="contained"
                      size="small"
                      disabled={saving}
                      startIcon={<Upload size={14} />}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" } }}
                    >
                      {thumbPreview ? "Change Image" : "Upload Image"}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        {...cardImageField}
                        onChange={(e) => {
                          cardImageField.onChange(e);
                          handleThumbChange(e);
                        }}
                      />
                    </Button>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                      Recommended: 400x250px, JPG/PNG
                    </Typography>
                  </Box>
                </Box>
              </Col>
            </SectionCard>
          </DialogContent>

          <DialogActions sx={{
            px: 3, py: 2.25,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "#FFFFFF",
            gap: 1.5,
          }}>
            <Button
              onClick={() => closeDialog()}
              disabled={saving}
              sx={{
                textTransform: "none", fontWeight: 700, color: "#475569",
                px: 2.5, py: 1, borderRadius: "10px",
                backgroundColor: "rgba(15,23,42,0.04)",
                "&:hover": { backgroundColor: "rgba(15,23,42,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                px: 3.5, py: 1.1, minWidth: 140,
                boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
                "&:hover": { background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)", boxShadow: "0 6px 18px rgba(37,99,235,0.4)" },
              }}
            >
              {saving ? "Saving..." : editing ? "Update Course" : "Create Course"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: "1px solid rgba(15,23,42,0.08)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this course?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary", pt: 1 }}>
            "{confirmDelete?.title}" will be removed from the public site immediately. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid rgba(15,23,42,0.08)" }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "10px",
              bgcolor: "#EF4444", px: 2.5, minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" },
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}